import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';

const DEMOS = [
  { role: 'manager', label: 'Landlord / Agent', icon: 'Building2', email: 'sarah@mitchell-lettings.co.uk' },
  { role: 'contractor', label: 'Contractor', icon: 'Wrench', email: 'james@checkaproperty.co.uk' },
  { role: 'admin', label: 'Admin', icon: 'ShieldCheck', email: 'admin@checkaproperty.co.uk' },
];

const Login = () => {
  const navigate = useNavigate();
  const { login, register, quickLogin } = useApp();
  const { toast } = useToast();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', password: '', org: '', role: 'manager' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'login') await login(form.email, form.password);
      else await register({ name: form.name, email: form.email, password: form.password, org: form.org, role: form.role });
      toast({ title: 'Signed in' });
      navigate('/dashboard');
    } catch (err) {
      toast({ title: 'Sign in failed', description: err?.response?.data?.detail || 'Check your details and try again.' });
    } finally { setBusy(false); }
  };

  const demo = async (role) => {
    setBusy(true);
    try { await quickLogin(role); navigate('/dashboard'); }
    catch (e) { toast({ title: 'Demo login failed' }); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex hero-gradient text-white p-12 flex-col justify-between">
        <Link to="/" className="flex items-center gap-2.5"><span className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center"><Icons.House className="h-5 w-5" /></span><span className="font-display font-extrabold text-lg">Check-a-Property</span></Link>
        <div>
          <h2 className="font-display font-bold text-4xl leading-tight">One platform for every property service.</h2>
          <p className="mt-4 text-slate-300 max-w-md">Book inventories, safety certificates and inspections, dispatch contractors, and track every job to completion with compliant reports.</p>
          <div className="mt-8 space-y-3">
            {['Rule-based contractor dispatch', 'Full 11-state job lifecycle', 'AI-assisted inspection reports'].map((t) => (
              <div key={t} className="flex items-center gap-3 text-slate-200"><Icons.CheckCircle2 className="h-5 w-5 text-[#7fb0f5]" /> {t}</div>
            ))}
          </div>
        </div>
        <div className="text-slate-400 text-sm">© 2025 Check-a-Property</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden font-display font-extrabold text-lg text-[#102d55] block mb-8">Check-a-Property</Link>
          <h1 className="font-display font-bold text-3xl text-[#102d55]">{mode === 'login' ? 'Welcome back' : 'Create your account'}</h1>
          <p className="text-muted-foreground mt-2">{mode === 'login' ? 'Sign in to your dashboard.' : 'Get started in seconds.'}</p>

          <div className="mt-6 grid grid-cols-3 gap-2">
            {DEMOS.map((d) => { const C = Icons[d.icon]; return (
              <button key={d.role} onClick={() => demo(d.role)} disabled={busy} className="rounded-xl border border-border p-3 flex flex-col items-center gap-1 hover:border-[#1c63c4] transition-colors">
                <C className="h-5 w-5 text-[#102d55]" /><span className="text-xs font-medium">{d.label}</span>
              </button>
            ); })}
          </div>
          <div className="text-center text-xs text-muted-foreground mt-2">One-click demo sign-in</div>

          <div className="mt-4 rounded-xl border border-border bg-secondary/40 p-3 text-xs">
            <div className="font-semibold text-[#102d55] mb-1.5 flex items-center gap-1.5"><Icons.KeyRound className="h-3.5 w-3.5" /> Demo credentials (password: <span className="font-mono">password123</span>)</div>
            <ul className="space-y-1 text-muted-foreground">
              <li><span className="font-medium text-slate-700">Landlord/Agent:</span> sarah@mitchell-lettings.co.uk</li>
              <li><span className="font-medium text-slate-700">Contractor:</span> james@checkaproperty.co.uk</li>
              <li><span className="font-medium text-slate-700">Admin:</span> admin@checkaproperty.co.uk</li>
            </ul>
          </div>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground"><div className="flex-1 h-px bg-border" /> or {mode === 'login' ? 'sign in' : 'register'} manually <div className="flex-1 h-px bg-border" /></div>

          <form onSubmit={submit} className="space-y-4">
            {mode === 'register' && <div><Label>Full name</Label><Input value={form.name} onChange={(e) => set('name', e.target.value)} className="mt-1.5" placeholder="Jane Smith" /></div>}
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="mt-1.5" placeholder="you@company.com" /></div>
            <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} className="mt-1.5" placeholder="Enter your password" /></div>
            {mode === 'register' && <div><Label>Organisation</Label><Input value={form.org} onChange={(e) => set('org', e.target.value)} className="mt-1.5" placeholder="Your company" /></div>}
            <Button type="submit" disabled={busy} className="w-full bg-[#102d55] hover:bg-[#0b2244]">{busy ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}</Button>
          </form>
          <p className="text-center text-sm text-muted-foreground mt-6">
            {mode === 'login' ? <>New here? <button onClick={() => setMode('register')} className="text-[#1c63c4] font-medium">Create an account</button></> : <>Have an account? <button onClick={() => setMode('login')} className="text-[#1c63c4] font-medium">Sign in</button></>}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
